import { useCallback, useEffect, useRef, useState } from "react";
import { ZohoSyncPanel, useZohoSync } from "./zoho-sync";
import type { SectionProps } from "./context";
import { Band, Head, cn, rv } from "./ui";
import { apiErrorText } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { useAuth } from "./auth";
import { goAnchor } from "./app-nav";
import { aed, companiesApi, showCompany, zohoApi, type Company, type ZohoCustomer } from "./companies-api";
import { locale, t } from "@/lib/i18n";

/* «Zoho»: синхронизация с Zoho CRM/Books (только чтение) и очередь сопоставления — клиенты Books, которых правила не связали
   с человеком или компанией Hub. Сильные совпадения (компания/контакт CRM, телефон, почта) связываются сами, подсказки подтверждает человек. */

const TABS = [
  { id: "open", label: t("Нужно связать") },
  { id: "no_person", label: t("Компания без человека") },
  { id: "linked", label: t("Связаны") },
  { id: "rejected", label: t("Не связано") },
] as const;

const input =
  "h-9 w-full rounded-btn border border-border bg-background px-2.5 text-[13px] text-foreground outline-none transition focus:border-primary";

function ZohoMatchDemo({ title, subtitle }: SectionProps) {
  const { user } = useAuth();
  const canLink = user?.role !== "viewer" && user?.role !== "agent";
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("open");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<ZohoCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [picking, setPicking] = useState<string | null>(null);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  }, []);

  const loadRows = useCallback(
    () =>
      zohoApi.customers({ status: tab, q, size: 100 }).then(
        (r) => {
          setRows(r.items);
          setTotal(r.total);
        },
        (e) => flash(apiErrorText(e)),
      ),
    [tab, q, flash],
  );

  // окончание синхронизации приходит уведомлением — перечитываем очередь сопоставления
  useEffect(() => onServerEvent("notification", () => void loadRows()), [loadRows]);
  const z = useZohoSync(flash);
  const { loadStatus } = z;

  const act = async (fn: () => Promise<ZohoCustomer>, text: string) => {
    try {
      await fn();
      setPicking(null);
      flash(text);
      void loadRows();
      void loadStatus();
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  return (
    <Band id="zoho-link" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv(1)} className="mt-6 overflow-hidden rounded-card border border-border bg-card p-3.5 shadow-sm md:p-5">
        <ZohoSyncPanel z={z} />
        <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
          {t("Сами связываются: компания или контакт Zoho CRM, совпавший телефон (включая WhatsApp) или почта. Подсказки — по домену корпоративной почты, TRN и названию. Карточки клиентов Books дочитываются постепенно (дневной лимит Zoho), поэтому часть подсказок превратится в связи сама.")}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  "h-8 cursor-pointer rounded-full border px-3 text-[12px] font-semibold transition",
                  tab === t.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Клиент, почта, TRN")} aria-label={t("Поиск клиента Books")} className={cn(input, "ms-auto max-w-xs")} />
        </div>
        <p className="mt-2 text-[12px] text-muted-foreground">{t("{n} покупателей · по выручке", { n: total.toLocaleString(locale) })}</p>

        <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
          {rows.map((z) => (
            <li key={z.booksId} className="px-3 py-2.5">
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-0 flex-1">
                  <a href={z.booksUrl} target="_blank" rel="noreferrer" className="text-[12.5px] font-semibold text-primary hover:underline">
                    {z.name}
                  </a>
                  <p className="text-[11.5px] text-muted-foreground">
                    {aed(z.revenue)} · {t("{n} счетов", { n: z.invoices })}{z.receivable >= 1 ? ` · ${t("долг {sum}", { sum: aed(z.receivable) })}` : ""}
                    {z.salesperson ? ` · ${z.salesperson}` : ""}
                    {[z.email, z.phone].filter(Boolean).length ? ` · ${[z.email, z.phone].filter(Boolean).join(", ")}` : ""}
                  </p>
                  {z.linkStatus === "linked" ? (
                    <p className="mt-0.5 text-[11.5px]">
                      {z.companyId ? (
                        <button
                          type="button"
                          onClick={() => {
                            showCompany(z.companyId!);
                            goAnchor("#companies");
                          }}
                          className="cursor-pointer font-semibold text-foreground hover:underline"
                        >
                          {z.companyTitle}
                        </button>
                      ) : null}
                      {z.contactTitle ? <span className="text-muted-foreground"> · {z.contactTitle}</span> : null}
                      <span className="text-muted-foreground"> · {z.linkWhy}</span>
                    </p>
                  ) : null}
                </div>
                {canLink ? (
                  <div className="flex flex-wrap gap-1.5">
                    {z.linkStatus === "linked" || z.linkStatus === "rejected" ? (
                      <SmallBtn onClick={() => void act(() => zohoApi.reset(z.booksId), t("Связь пересчитана по правилам"))}>{t("Пересчитать")}</SmallBtn>
                    ) : (
                      <>
                        <SmallBtn onClick={() => setPicking(picking === z.booksId ? null : z.booksId)}>{t("Выбрать компанию")}</SmallBtn>
                        <SmallBtn onClick={() => void act(() => zohoApi.reject(z.booksId), t("Отмечено: не связано"))}>{t("Не связано")}</SmallBtn>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
              {z.suggestions.length > 0 && z.linkStatus === "suggested" ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {z.suggestions.map((s) => (
                    <button
                      key={`${s.kind}-${s.id}`}
                      type="button"
                      disabled={!canLink}
                      onClick={() => void act(() => zohoApi.link(z.booksId, s.kind === "company" ? { companyId: s.id } : { contactId: s.id }), t("Связано с «{name}»", { name: s.name }))}
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-btn border border-amber-500/40 bg-amber-500/5 px-2.5 py-1.5 text-start text-[12px] transition hover:border-primary disabled:cursor-default"
                    >
                      <i className={s.kind === "company" ? "ri-building-2-line" : "ri-user-line"} aria-hidden />
                      <span className="font-semibold">{s.name}</span>
                      <span className="text-muted-foreground">— {s.why}</span>
                      {canLink ? <span className="font-semibold text-primary">{t("Связать")}</span> : null}
                    </button>
                  ))}
                </div>
              ) : null}
              {picking === z.booksId ? <CompanyPicker initial={z.companyName || z.name} onPick={(c) => void act(() => zohoApi.link(z.booksId, { companyId: c.id }), t("Связано с «{name}»", { name: c.name }))} /> : null}
            </li>
          ))}
          {rows.length === 0 ? <li className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">{t("Здесь пусто.")}</li> : null}
        </ul>
      </div>
      {toast ? (
        <div role="status" className="fixed bottom-5 start-1/2 z-50 -translate-x-1/2 rounded-btn bg-foreground px-4 py-2.5 text-[13px] text-background shadow-lg">
          {toast}
        </div>
      ) : null}
    </Band>
  );
}

function SmallBtn({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-8 cursor-pointer items-center whitespace-nowrap rounded-btn border border-border bg-background px-2.5 text-[12px] font-semibold text-foreground transition hover:bg-muted"
    >
      {children}
    </button>
  );
}

function CompanyPicker({ initial, onPick }: { initial: string; onPick: (c: Company) => void }) {
  const [q, setQ] = useState(initial.split(/\s+/).slice(0, 2).join(" "));
  const [rows, setRows] = useState<Company[]>([]);
  useEffect(() => {
    const t = setTimeout(() => companiesApi.list({ q, size: 8 }).then((r) => setRows(r.items), () => undefined), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="mt-2 rounded-btn border border-border bg-background/60 p-2">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} aria-label={t("Найти компанию")} className={input} />
      <ul className="mt-1.5 flex flex-col">
        {rows.map((c) => (
          <li key={c.id}>
            <button type="button" onClick={() => onPick(c)} className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[12.5px] transition hover:bg-muted">
              <span className="min-w-0 flex-1 truncate font-semibold">{c.name}</span>
              <span className="text-[11.5px] text-muted-foreground">{[c.country, t("{n} чел.", { n: c.people })].filter(Boolean).join(" · ")}</span>
            </button>
          </li>
        ))}
        {rows.length === 0 ? <li className="px-2 py-1.5 text-[12px] text-muted-foreground">{t("Не найдено — создайте компанию в разделе «Компании».")}</li> : null}
      </ul>
    </div>
  );
}

export default ZohoMatchDemo;
